import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { fetchMonthlyWaste } from "../../store/actions/dashboard";
import { currentYear } from "../../utils/tools";
import { Spinner } from "react-bootstrap";

const SELECT_STYLE = {
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: "6px 12px",
  color: "#0B1157",
  fontSize: 14,
  cursor: "pointer",
  outline: "none",
};

const fmt = (num) => String(num).replace(".", ",");

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 10,
        padding: "10px 16px",
        boxShadow: "0 4px 16px rgba(11,17,87,0.10)",
        color: "#0B1157",
        fontSize: 14,
      }}
    >
      <p style={{ margin: 0, fontWeight: 600, marginBottom: 6 }}>{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ margin: "2px 0", color: entry.color }}>
          {entry.name}: <strong>{fmt(entry.value)} t</strong>
        </p>
      ))}
    </div>
  );
};

const YEAR = currentYear();

const WasteTrendChart = () => {
  const dispatch = useDispatch();
  const branches = useSelector((state) => state.branch.data);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(YEAR);
  const [branchId, setBranchId] = useState("");

  useEffect(() => {
    setLoading(true);
    dispatch(fetchMonthlyWaste({ year, branchId: branchId || null }))
      .unwrap()
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dispatch, year, branchId]);

  const years = Array.from({ length: 5 }, (_, i) => YEAR - i);

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        padding: "24px 24px 16px",
        boxShadow: "0 2px 12px rgba(11,17,87,0.07)",
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h6 style={{ color: "#0B1157", fontWeight: 700, margin: 0, fontSize: 16 }}>
            Príjem vs Výdaj odpadu
          </h6>
          <p style={{ color: "#718094", fontSize: 13, margin: 0 }}>
            Mesačný prehľad v tonách
          </p>
        </div>
        <div className="d-flex gap-2">
          <select
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            style={SELECT_STYLE}
          >
            <option value="">Všetky prevádzkarne</option>
            {branches.map((b) => (
              <option key={b._id} value={b._id}>
                {b.name}
              </option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={SELECT_STYLE}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: 280 }}
        >
          <Spinner animation="border" style={{ color: "#0B1157" }} />
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0B1157" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#0B1157" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="disposalGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4DFFFF" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#4DFFFF" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fill: "#718094", fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: "#718094", fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              unit=" t"
              width={55}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 13, color: "#718094", paddingTop: 12 }} />
            <Area
              type="monotone"
              dataKey="income"
              name="Príjem"
              stroke="#0B1157"
              strokeWidth={2}
              fill="url(#incomeGrad)"
              dot={false}
              activeDot={{ r: 5, fill: "#0B1157" }}
            />
            <Area
              type="monotone"
              dataKey="disposal"
              name="Výdaj"
              stroke="#4DFFFF"
              strokeWidth={2}
              fill="url(#disposalGrad)"
              dot={false}
              activeDot={{ r: 5, fill: "#4DFFFF" }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default WasteTrendChart;
